/**
 * Disjoint Force-Directed Graph
 * Based on Observable HQ pattern: https://observablehq.com/@d3/disjoint-force-directed-graph
 * Shows separate clusters for different teams/groups
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography } from '@mui/material';
import useTokenExaminerStore from '../../stores/tokenExaminerStore';
import { getFaanaHealthColor } from '../../utils/faanaScoring';

// eslint-disable-next-line max-lines-per-function
function DisjointForceGraph() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const {
    _relationships,
    entities,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Render chart
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || !filteredRelationships.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Prepare data with team groupings
    const nodes = Array.from(entities.values()).map(entity => {
      // Determine team based on role/type
      let team = 'other';
      if (entity.name.includes('Engineering') || entity.name.includes('Engineer') ||
          ['Chris Taylor', 'Priya Patel', 'Marcus Johnson', 'Amy Chen', 'Kevin Chen', 'Sarah Wilson', 'James Mitchell'].includes(entity.name)) {
        team = 'engineering';
      } else if (entity.name.includes('Sales') || entity.name.includes('Revenue') ||
                 ['Daniel Park', 'Robert Martinez', 'Christina Walsh', 'David Johnson'].includes(entity.name)) {
        team = 'sales';
      } else if (entity.name.includes('Marketing') ||
                 ['Sandra Cooper'].includes(entity.name)) {
        team = 'marketing';
      } else if (entity.name.includes('CEO') || entity.name.includes('CFO') || entity.name.includes('Board') ||
                 ['Emily Carter', 'Maria Gonzalez'].includes(entity.name)) {
        team = 'leadership';
      }

      return {
        id: entity.crossEntityHash,
        name: entity.name,
        type: entity.type,
        team,
        relationships: filteredRelationships.filter(r =>
          (r.sourceEntity?.crossEntityHash || r.sourceEntityHash) === entity.crossEntityHash ||
          (r.targetEntity?.crossEntityHash || r.targetEntityHash) === entity.crossEntityHash
        ).length
      };
    });

    const links = filteredRelationships.map(rel => ({
      source: rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash,
      target: rel.targetEntity?.crossEntityHash || rel.targetEntityHash,
      strength: rel.overallHealth || 50,
      type: rel.relationshipType,
    }));

    // Team colors
    const teamColors = {
      engineering: '#2E7D32',
      sales: '#1976D2',
      marketing: '#7B1FA2',
      leadership: '#D32F2F',
      other: '#616161'
    };

    // Create separate simulations for each team
    const teams = [...new Set(nodes.map(d => d.team))];
    const teamCenters = {};
    const teamRadius = 80;

    teams.forEach((team, i) => {
      const angle = (i / teams.length) * 2 * Math.PI;
      const centerDistance = Math.min(dimensions.width, dimensions.height) * 0.25;
      teamCenters[team] = {
        x: dimensions.width / 2 + Math.cos(angle) * centerDistance,
        y: dimensions.height / 2 + Math.sin(angle) * centerDistance
      };
    });

    // Create main container
    const container = svg.append('g');

    // Add team background circles
    teams.forEach(team => {
      const center = teamCenters[team];
      container.append('circle')
        .attr('cx', center.x)
        .attr('cy', center.y)
        .attr('r', teamRadius)
        .attr('fill', teamColors[team])
        .attr('opacity', 0.1)
        .attr('stroke', teamColors[team])
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');

      // Add team labels
      container.append('text')
        .attr('x', center.x)
        .attr('y', center.y - teamRadius - 10)
        .attr('text-anchor', 'middle')
        .style('font-weight', 'bold')
        .style('font-size', '14px')
        .style('fill', teamColors[team])
        .text(team.charAt(0).toUpperCase() + team.slice(1));
    });

    // Create links
    const linkElements = container.selectAll('.link')
      .data(links)
      .enter().append('line')
      .attr('class', 'link')
      .attr('stroke', d => getFaanaHealthColor(d.strength))
      .attr('stroke-width', d => Math.max(1, d.strength / 25))
      .attr('opacity', 0.6);

    // Create nodes
    const nodeElements = container.selectAll('.node')
      .data(nodes)
      .enter().append('g')
      .attr('class', 'node');

    nodeElements.append('circle')
      .attr('r', d => 8 + Math.sqrt(d.relationships) * 2)
      .attr('fill', d => teamColors[d.team])
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    nodeElements.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .style('font-size', '10px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .style('pointer-events', 'none')
      .text(d => d.name.split(' ').map(w => w[0]).join(''));

    // Create force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(40))
      .force('charge', d3.forceManyBody().strength(-100))
      .force('collision', d3.forceCollide().radius(d => 12 + Math.sqrt(d.relationships) * 2))
      .force('team', d3.forceRadial(teamRadius * 0.5, d => teamCenters[d.team].x, d => teamCenters[d.team].y).strength(0.8))
      .alphaDecay(0.02);

    // Update positions on tick
    simulation.on('tick', () => {
      linkElements
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      nodeElements
        .attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Add drag behavior
    nodeElements.call(d3.drag()
      .on('start', (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      }));

    // Cleanup
    return () => {
      simulation.stop();
    };

  }, [dimensions, filteredRelationships, entities]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 2,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Teams
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            { label: 'Engineering', color: '#2E7D32' },
            { label: 'Sales', color: '#1976D2' },
            { label: 'Marketing', color: '#7B1FA2' },
            { label: 'Leadership', color: '#D32F2F' },
            { label: 'Other', color: '#616161' },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                }}
              />
              <Typography variant="caption">{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

export default DisjointForceGraph;